import React, { memo, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
    WidgetContentLayout,
    WidgetHeaderLayout,
    WidgetLayout,
} from 'containers/components/widgets/Layouts';
import SalesOrdersProductLinesTable from 'containers/SalesOrders/components/SalesOrdersProductLinesTable';
import { getLiteral } from 'utils/getLiteral';
import './styles.scss';

const SalesOrderProductLinesWidget = memo(({ data, products, setSize }) => {
    const salesOrderAmountDiscounts = useSelector(
        (state) => !!state?.config?.userData?.salesOrderAmountDiscounts,
    );

    // --- Refs
    const widgetRef = useRef(null);
    const prevProductsLength = useRef(data.products?.length);
    const lastReportedHeight = useRef(0);

    // --- Derived state
    const productsLength = data.products?.length ?? 0;

    const salesOrderContext = useMemo(
        () => ({
            idEnvironment: data?.idBranch,
            idCurrency: data?.idCurrency,
            currencySymbol: data?.currencySymbol,
        }),
        [data?.idBranch, data?.idCurrency, data?.currencySymbol],
    );

    const tableParams = useMemo(
        () => ({
            salesOrderData: salesOrderContext,
            salesOrderAmountDiscounts,
        }),
        [salesOrderAmountDiscounts, salesOrderContext],
    );

    // --- Effects
    useEffect(() => {
        if (!setSize || !widgetRef.current) return;

        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const { height, width } = entry.contentRect;
                if (height !== lastReportedHeight.current && height > 0) {
                    lastReportedHeight.current = height;
                    setSize({ width, height }, false, true);
                }
            }
        });

        resizeObserver.observe(widgetRef.current);

        return () => resizeObserver.disconnect();
    }, [setSize]);

    useEffect(() => {
        const currentLength = productsLength;
        const prevLength = prevProductsLength.current ?? 0;

        prevProductsLength.current = currentLength;

        if (currentLength < prevLength && setSize && widgetRef.current) {
            requestAnimationFrame(() => {
                if (!widgetRef.current) return;
                const height = widgetRef.current.offsetHeight;
                const width = widgetRef.current.offsetWidth;
                lastReportedHeight.current = height;
                setSize({ width, height }, false, true);
            });
        }
    }, [productsLength, setSize]);

    // --- Render
    return (
        <div ref={widgetRef}>
            <WidgetLayout
                className="sales-order-product-lines-widget"
                data={data}
                isDynamic={true}
                setSize={setSize}
                type="generalInfo"
            >
                <WidgetHeaderLayout>
                    <div className="sales-order-product-lines-widget__header">
                        {getLiteral('common_lines_saleorders')}
                        {products && (
                            <span className="sales-order-product-lines-widget__header__counter">
                                ({products?.length ?? 0})
                            </span>
                        )}
                    </div>
                </WidgetHeaderLayout>
                <WidgetContentLayout>
                    <div className="sales-order-product-lines-widget__content">
                        <div className="sales-order-product-lines-widget__content__table">
                            <SalesOrdersProductLinesTable
                                data={data.products}
                                tableParams={tableParams}
                            />
                        </div>
                    </div>
                </WidgetContentLayout>
            </WidgetLayout>
        </div>
    );
});

SalesOrderProductLinesWidget.displayName = 'SalesOrderProductLinesWidget';

export default SalesOrderProductLinesWidget;
